import { UserManagementApi } from '@liks/backend-svc-api/esm/apis/UserManagementApi';
import type {
  UserGroup,
  UserLabel,
} from '@liks/backend-svc-api/esm/models/index';
import type { KeycloakClientRole } from '../auth/clientRoles';
import type { Locale } from '../i18n/resources';
import type { WithResourceId } from '../types/knowledge';
import type { CurrentUserProfile } from '../types/user';
import { backendApiConfiguration } from './backendApi';
import * as userDirectoryOps from './userDirectoryOps';
import * as userGroupOps from './userGroupOps';
import * as userLabelOps from './userLabelOps';
import type {
  AssignedDirectoryUser,
  CreateDirectoryUserInput,
  CreateDirectoryUserResult,
  DirectoryUserWithId,
  SetDirectoryUserEnabledInput,
  UpdateDirectoryUserInput,
} from './userServiceTypes';

export type {
  AssignedDirectoryUser,
  CreateDirectoryUserInput,
  CreateDirectoryUserResult,
  CurrentUserProfile,
  DirectoryUser,
  DirectoryUserWithId,
  SetDirectoryUserEnabledInput,
  UpdateDirectoryUserInput,
} from './userServiceTypes';

class UserService {
  private readonly userApi = new UserManagementApi(backendApiConfiguration);

  getCurrentUser(): Promise<CurrentUserProfile> {
    return userDirectoryOps.getCurrentUser(this.userApi);
  }

  updateCurrentUserLanguage(locale: Locale): Promise<void> {
    return userDirectoryOps.updateCurrentUserLanguage(this.userApi, locale);
  }

  getUser(userId: string): Promise<CurrentUserProfile> {
    return userDirectoryOps.getUser(this.userApi, userId);
  }

  listUsers(): Promise<DirectoryUserWithId[]> {
    return userDirectoryOps.listUsers(this.userApi);
  }

  createUser(
    input: CreateDirectoryUserInput,
  ): Promise<CreateDirectoryUserResult> {
    return userDirectoryOps.createUser(this.userApi, input);
  }

  updateUser(input: UpdateDirectoryUserInput): Promise<void> {
    return userDirectoryOps.updateUser(this.userApi, input);
  }

  getUserRoles(userId: string): Promise<string[]> {
    return userDirectoryOps.getUserRoles(this.userApi, userId);
  }

  setUserEnabled(input: SetDirectoryUserEnabledInput): Promise<void> {
    return userDirectoryOps.setUserEnabled(this.userApi, input);
  }

  deleteUser(userId: string): Promise<void> {
    return userDirectoryOps.deleteUser(this.userApi, userId);
  }

  resetUserPassword(
    userId: string,
    password: string,
    temporary = true,
  ): Promise<void> {
    return userDirectoryOps.resetUserPassword(
      this.userApi,
      userId,
      password,
      temporary,
    );
  }

  updateCurrentUserPassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    return userDirectoryOps.updateCurrentUserPassword(
      this.userApi,
      currentPassword,
      newPassword,
    );
  }

  downloadUserPhoto(userId: string): Promise<Blob | null | undefined> {
    return userDirectoryOps.downloadUserPhoto(this.userApi, userId);
  }

  uploadUserPhoto(userId: string, file: File): Promise<void> {
    return userDirectoryOps.uploadUserPhoto(this.userApi, userId, file);
  }

  deleteUserPhoto(userId: string): Promise<void> {
    return userDirectoryOps.deleteUserPhoto(this.userApi, userId);
  }

  setUserRoles(
    userId: string,
    roleNames: readonly KeycloakClientRole[],
  ): Promise<void> {
    return userDirectoryOps.setUserRoles(this.userApi, userId, roleNames);
  }

  listLabels(): Promise<Array<WithResourceId<UserLabel> & { name: string }>> {
    return userLabelOps.listLabels(this.userApi);
  }

  createLabel(name: string): Promise<UserLabel> {
    return userLabelOps.createLabel(this.userApi, name);
  }

  renameLabel(labelId: string, name: string): Promise<UserLabel> {
    return userLabelOps.renameLabel(this.userApi, labelId, name);
  }

  deleteLabel(labelId: string): Promise<void> {
    return userLabelOps.deleteLabel(this.userApi, labelId);
  }

  listUserLabels(
    userId: string,
  ): Promise<Array<WithResourceId<UserLabel> & { name: string }>> {
    return userLabelOps.listUserLabels(this.userApi, userId);
  }

  listLabelUsers(labelId: string): Promise<AssignedDirectoryUser[]> {
    return userLabelOps.listLabelUsers(this.userApi, labelId);
  }

  assignUserLabel(userId: string, labelId: string): Promise<void> {
    return userLabelOps.assignUserLabel(this.userApi, userId, labelId);
  }

  unassignUserLabel(userId: string, labelId: string): Promise<void> {
    return userLabelOps.unassignUserLabel(this.userApi, userId, labelId);
  }

  listGroups(): Promise<Array<WithResourceId<UserGroup> & { name: string }>> {
    return userGroupOps.listGroups(this.userApi);
  }

  createGroup(name: string): Promise<UserGroup> {
    return userGroupOps.createGroup(this.userApi, name);
  }

  renameGroup(groupId: string, name: string): Promise<UserGroup> {
    return userGroupOps.renameGroup(this.userApi, groupId, name);
  }

  deleteGroup(groupId: string): Promise<void> {
    return userGroupOps.deleteGroup(this.userApi, groupId);
  }

  listUserGroups(
    userId: string,
  ): Promise<Array<WithResourceId<UserGroup> & { name: string }>> {
    return userGroupOps.listUserGroups(this.userApi, userId);
  }

  listGroupMembers(groupId: string): Promise<AssignedDirectoryUser[]> {
    return userGroupOps.listGroupMembers(this.userApi, groupId);
  }

  assignUserGroup(userId: string, groupId: string): Promise<void> {
    return userGroupOps.assignUserGroup(this.userApi, userId, groupId);
  }

  unassignUserGroup(userId: string, groupId: string): Promise<void> {
    return userGroupOps.unassignUserGroup(this.userApi, userId, groupId);
  }
}

export const userService = new UserService();
